/**
 * API для загрузки отзывов (kit_reviews) из Supabase
 */

import { useConfigStore } from '@/store/configStore';
import { chatLogger } from '@/utils/chatLogger';

export interface ReviewSection {
  subtitle?: string;
  text: string;
}

export interface KitReview {
  id: string;
  kit_id: string;
  title: string;
  title_en: string | null;
  title_de: string | null;
  sections: ReviewSection[];
  sections_en: ReviewSection[];
  sections_de: ReviewSection[];
  images: string[];
  sort_order: number;
}

const cache = new Map<string, { data: KitReview[]; ts: number }>();
const CACHE_TTL = 5 * 60 * 1000;
const MAX_CREDENTIAL_WAIT = 5000;
const CREDENTIAL_POLL = 200;

function getSupabaseCredentials(): { url: string; key: string } | null {
  const { config } = useConfigStore.getState();
  const url = config.supabaseUrl || import.meta.env.VITE_SUPABASE_URL;
  const key = config.supabaseAnonKey || import.meta.env.VITE_SUPABASE_ANON_KEY;
  return url && key ? { url, key } : null;
}

async function waitForCredentials(): Promise<{ url: string; key: string } | null> {
  const immediate = getSupabaseCredentials();
  if (immediate) return immediate;

  let elapsed = 0;
  return new Promise((resolve) => {
    const interval = setInterval(() => {
      elapsed += CREDENTIAL_POLL;
      const creds = getSupabaseCredentials();
      if (creds) { clearInterval(interval); resolve(creds); }
      else if (elapsed >= MAX_CREDENTIAL_WAIT) { clearInterval(interval); resolve(null); }
    }, CREDENTIAL_POLL);
  });
}

/**
 * Загружает отзывы по коду комплекта (kit.code).
 * Ждёт до 5 сек пока Supabase credentials появятся в store.
 * Кэширует только непустые успешные результаты.
 */
export async function fetchReviewsByKitCode(kitCode: string): Promise<KitReview[]> {
  const cached = cache.get(kitCode);
  if (cached && Date.now() - cached.ts < CACHE_TTL) return cached.data;

  const creds = await waitForCredentials();
  if (!creds) {
    chatLogger.warn('[Reviews API] Supabase not configured after waiting');
    return [];
  }

  const headers = {
    apikey: creds.key,
    Authorization: `Bearer ${creds.key}`,
  };

  try {
    const kitRes = await fetch(
      `${creds.url}/rest/v1/kits?code=eq.${encodeURIComponent(kitCode)}&select=id&limit=1`,
      { headers },
    );
    if (!kitRes.ok) return [];
    const kits = await kitRes.json();
    if (!kits.length) return [];

    const kitId = kits[0].id;

    const reviewsRes = await fetch(
      `${creds.url}/rest/v1/kit_reviews?kit_id=eq.${kitId}&is_active=eq.true&order=sort_order.asc&select=id,kit_id,title,title_en,title_de,sections,sections_en,sections_de,images,sort_order`,
      { headers },
    );
    if (!reviewsRes.ok) return [];
    const data: KitReview[] = await reviewsRes.json();

    if (data.length > 0) {
      cache.set(kitCode, { data, ts: Date.now() });
    }
    return data;
  } catch (err) {
    chatLogger.error('[Reviews API] Error:', err);
    return [];
  }
}
